import React, { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { SurveyAnalysisRequest } from '../types/survey';
import { RUNTIME_DEFAULT_MODEL, RUNTIME_MODELS } from '../constants/models';

interface SurveyFormProps {
  onSubmit: (request: SurveyAnalysisRequest) => void;
  loading: boolean;
}

const SurveyForm: React.FC<SurveyFormProps> = ({ onSubmit, loading }) => {
  const [formData, setFormData] = useState<SurveyAnalysisRequest>({
    survey_url: '',
    model_name: RUNTIME_DEFAULT_MODEL,
    max_steps: 150,
    use_vision: true
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    // Validate URL
    if (!formData.survey_url.trim()) {
      newErrors.survey_url = 'Survey URL is required';
    } else {
      try {
        new URL(formData.survey_url);
      } catch {
        newErrors.survey_url = 'Please enter a valid URL';
      }
    }

    // Validate max steps
    if (formData.max_steps < 10 || formData.max_steps > 500) {
      newErrors.max_steps = 'Max steps must be between 10 and 500';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleInputChange = (field: keyof SurveyAnalysisRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="mb-6">
        <p className="text-sm text-gray-600">
          To check a survey, provide it below. See the FAQ page for more information about what Survey Shield can and cannot do.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Survey URL */}
        <div>
          <label htmlFor="survey_url" className="block text-sm font-medium text-gray-700 mb-2">
            Survey URL *
          </label>
          <input
            type="url"
            id="survey_url"
            value={formData.survey_url}
            onChange={(e) => handleInputChange('survey_url', e.target.value)}
            placeholder="https://example.com/survey"
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.survey_url ? 'border-red-500' : 'border-gray-300'
              }`}
            disabled={loading}
          />
          {errors.survey_url && (
            <p className="mt-1 text-sm text-red-600 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              {errors.survey_url}
            </p>
          )}
        </div>

        {/* Advanced Settings Toggle */}
        <div>
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            {showAdvanced ? 'Hide' : 'Show'} Advanced Settings
          </button>
        </div>

        {/* Advanced Settings */}
        {showAdvanced && (
          <div className="bg-gray-50 p-4 rounded-lg space-y-4">
            {/* Model Selection */}
            <div>
              <label htmlFor="model_name" className="block text-sm font-medium text-gray-700 mb-2">
                Analysis Model
              </label>
              <select
                id="model_name"
                value={formData.model_name}
                onChange={(e) => handleInputChange('model_name', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                disabled={loading}
              >
                {RUNTIME_MODELS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>

            {/* Max Steps */}
            <div>
              <label htmlFor="max_steps" className="block text-sm font-medium text-gray-700 mb-2">
                Maximum Steps ({formData.max_steps})
              </label>
              <input
                type="range"
                id="max_steps"
                min="10"
                max="500"
                value={formData.max_steps}
                onChange={(e) => handleInputChange('max_steps', parseInt(e.target.value))}
                className="w-full"
                disabled={loading}
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>10 (Quick)</span>
                <span>500 (Thorough)</span>
              </div>
              {errors.max_steps && (
                <p className="mt-1 text-sm text-red-600">{errors.max_steps}</p>
              )}
            </div>

            {/* Vision */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="use_vision"
                checked={formData.use_vision}
                onChange={(e) => handleInputChange('use_vision', e.target.checked)}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                disabled={loading}
              />
              <label htmlFor="use_vision" className="ml-2 text-sm text-gray-700">
                Enable visual analysis
              </label>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className={`w-full flex items-center justify-center px-6 py-3 rounded-lg text-white font-medium transition-colors ${loading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-primary-600 hover:bg-primary-700 focus:ring-2 focus:ring-primary-500'
            }`}
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Analyzing...
            </>
          ) : (
            <>
              Analyze
            </>
          )}
        </button>

        {/* Estimated Time */}
        <p className="text-sm text-gray-500 text-center">
          Analysis may take 2-5 minutes depending on survey complexity.
        </p>
      </form>
    </div>
  );
};

export default SurveyForm;